import { getPlansArr, getReport } from "services/api";
import { getPBMs } from "services/api/pbms.api";
import model from "./model";
import initialValues from "./initialValues";
import { message } from "antd";

const {
  formField: { pharmacyManagersTable, drugsTable, table, group },
} = model;

const getDrugsMember = (member) => {
  if (member === "birthParent") return "Self";
  else if (member === "partner") return "Spouse";
  else if (member === "child") return "Child 1";
  else if (member.match(/child_\d+/)) return "Child " + member.split("_")[1];
  else if (member === "baby") return "Baby";

  return "Self";
};

export const loadValues = async ({ reportId, token }) => {
  if (!reportId) {
    message.error("No report id was provided");
    return initialValues;
  }
  const { data: reportInfo, status } = await getReport(reportId, token);
  if (status !== 200) {
    message.error("Report not found");
    return initialValues;
  }
  const report = reportInfo.report;

  const { data: bpPlanInfo } = await getPlansArr(report["birthParent"].planIds, token, false);
  const bpPlans = bpPlanInfo.plansArr;
  const { data: partnerPlanInfo } = await getPlansArr(report["partner"].planIds, token, false);
  const partnerPlans = report["partner"].planIds.length > 0 ? partnerPlanInfo.plansArr : [];
  const plans = [...bpPlans, ...partnerPlans];

  const PBMIds = [...new Set(plans.map((plan) => plan.pbmId))];
  const { data: pbmInfo } = await getPBMs({ idList: PBMIds, token });
  const pbms = pbmInfo.pbms;

  const pharmacyManagersTableMap =
    pbms && pbms.length
      ? pbms.map((pbm, index) => {
          return {
            id: pbm._id,
            tableName: pharmacyManagersTable.name,
            idx: index + 1,
            name: pbm.name,
            planList: plans.filter((plan) => plan.pbmId === pbm._id).map((plan) => plan.network),
            numberOfTiers: pbm.tiers.length,
            typesOfPharmacies: pbm.pharmacyTypes.length,
            tierCodes: pbm.tiers.map((tier) => tier.code),
            tierNames: pbm.tiers.map((tier) => tier.name),
            pharmacyTypes: pbm.pharmacyTypes.map((type) => type.type),
            maxSupply: pbm.pharmacyTypes.map((type) => type.maxSupply),
          };
        })
      : initialValues.pharmacyManagersTable;

  const tableMap = plans.map((plan, index) => {
    const adjustCoinsuranceRate = (coverage) =>
      Object.fromEntries(
        Object.entries(coverage).map(([key, value]) => [
          key,
          {
            ...value,
            coinsuranceRate: {
              value: value.coinsuranceRate.value * 100,
              min: value.coinsuranceRate.min,
              max: value.coinsuranceRate.max,
            },
          },
        ]),
      );

    const retail = plan.drugs?.coverage?.retail ? adjustCoinsuranceRate(plan.drugs.coverage.retail) : {};
    const mailOrder = plan.drugs?.coverage?.mailOrder ? adjustCoinsuranceRate(plan.drugs.coverage.mailOrder) : {};
    const preferredRetail = plan.drugs?.coverage?.preferredRetail
      ? adjustCoinsuranceRate(plan.drugs.coverage.preferredRetail)
      : {};
    const preferredMailOrder = plan.drugs?.coverage?.preferredMailOrder
      ? adjustCoinsuranceRate(plan.drugs.coverage.preferredMailOrder)
      : {};

    return {
      id: plan.externalId,
      tableName: table.name,
      idx: index + 1,
      planName: plan.name,
      label: plan.network,
      insuranceNetwork: plan.insuranceCompany
        ? `${plan.broaderNetwork};${plan.insuranceCompany}`
        : plan.insuranceCompany === "Blue Cross/Blue Shield"
        ? "Blue Cross and/or Blue Shield (including Anthem)"
        : plan.broaderNetwork,
      deductibleType: plan.drugs.deductibleType,
      oopType: plan.drugs.oopType,
      crossAccumulate: plan.drugs.crossAccumulate,
      individualDeductible: plan.drugs.individual.deductible,
      individualOOPMax: plan.drugs.individual.oopMax,
      familyDeductible: plan.drugs.family.deductible,
      familyOOPMax: plan.drugs.family.oopMax,
      retail,
      mailOrder,
      preferredRetail,
      preferredMailOrder,
      tiers: plan.drugs.tiers,
    };
  });

  const drugsMap = report.expected.drugs?.length
    ? report.expected.drugs.map((drug, index) => {
        return {
          idx: index + 1,
          name: drug.name,
          cost: drug.charge,
          for: getDrugsMember(drug.member),
          frequency: drug.frequency,
          tableName: drugsTable.name,
          ...drug.tiers,
        };
      })
    : initialValues.drugsTable;

  return {
    ...initialValues,
    report: report,
    [table.name]: tableMap,
    [pharmacyManagersTable.name]: pharmacyManagersTableMap,
    [drugsTable.name]: drugsMap,
  };
};
